/**
 * easyCpf.js — Gerador de CPFs matematicamente válidos com PADRÕES VISUAIS FÁCEIS.
 * Uso exclusivo da página /nome-cpf. Não substitui o gerador aleatório da home.
 *
 * Estratégia:
 * 1) Gera N candidatos de 9 dígitos usando categorias diferentes de padrões.
 * 2) Pontua cada candidato pela facilidade de digitar (dígitos únicos, repetição, sequência…).
 * 3) Escolhe aleatoriamente entre os top K (weighted) para variar.
 * 4) Calcula os 2 dígitos verificadores.
 * 5) Retorna string formatada XXX.XXX.XXX-XX.
 */

// ---------- utils ----------
const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

// Bias forte para começar com 0 (45%), depois 1 (15%), depois qualquer outro
const pickStartDigit = () => {
  const r = Math.random();
  if (r < 0.45) return 0;
  if (r < 0.6) return 1;
  return randInt(0, 9);
};

// Pega um dígito qualquer 0-9
const anyDigit = () => randInt(0, 9);

// ---------- geradores de padrões (retornam array de 9 dígitos) ----------

// Padrão ABCABCABC — 3 dígitos que se repetem em bloco (ex: 123123123)
function patABCABCABC() {
  const a = pickStartDigit();
  const b = anyDigit();
  const c = anyDigit();
  return [a, b, c, a, b, c, a, b, c];
}

// Padrão AAABBBCCC — trios repetidos (ex: 000111222)
function patAAABBBCCC() {
  const a = pickStartDigit();
  const b = anyDigit();
  const c = anyDigit();
  return [a, a, a, b, b, b, c, c, c];
}

// Padrão AABBCCDDE — pares repetidos (ex: 001122334)
function patAABBCCDDE() {
  const a = pickStartDigit();
  const b = anyDigit();
  const c = anyDigit();
  const d = anyDigit();
  const e = anyDigit();
  return [a, a, b, b, c, c, d, d, e];
}

// Padrão ABABABABC — alternância (ex: 010101010, 121212121)
function patABABABABC() {
  const a = pickStartDigit();
  let b = anyDigit();
  if (b === a) b = (a + 1) % 10;
  const c = Math.random() < 0.5 ? a : b;
  return [a, b, a, b, a, b, a, b, c];
}

// Sequência crescente a partir de um dígito (ex: 012345678, 123456789)
function patCrescente() {
  const start = randInt(0, 1); // maior chance de começar 0 ou 1
  return Array.from({ length: 9 }, (_, i) => (start + i) % 10);
}

// Sequência decrescente (ex: 987654321)
function patDecrescente() {
  const start = randInt(8, 9);
  return Array.from({ length: 9 }, (_, i) => (start - i + 10) % 10);
}

// Blocos de 100 (ex: 100200300, 010020030)
function patBlocos100() {
  const a = anyDigit();
  const b = anyDigit();
  const c = anyDigit();
  // padrão "A00 B00 C00" ou variação "0A0 0B0 0C0"
  if (Math.random() < 0.5) return [a, 0, 0, b, 0, 0, c, 0, 0];
  return [0, a, 0, 0, b, 0, 0, c, 0];
}

// Muitos zeros com um dígito diferente (ex: 001001001)
function patManyZeros() {
  const a = anyDigit() || 1;
  const patterns = [
    [0, 0, a, 0, 0, a, 0, 0, a],
    [0, a, 0, 0, a, 0, 0, a, 0],
    [0, 0, 0, a, a, a, 0, 0, 0],
    [0, 0, 0, 0, a, 0, 0, 0, 0],
  ];
  return pick(patterns);
}

// Dois dígitos alternando em bloco (ex: 001100110)
function patDoisDigitos() {
  const a = pickStartDigit();
  let b = anyDigit();
  if (b === a) b = (a + 1) % 10;
  const patterns = [
    [a, a, b, b, a, a, b, b, a],
    [a, b, b, a, a, b, b, a, a],
    [a, a, a, b, b, b, a, a, a],
    [a, b, a, b, a, b, a, b, b],
  ];
  return pick(patterns);
}

// Escadinha (ex: 012012012 ou 123123123 é ABCABC — vamos variar com escadas curtas)
function patEscadinha() {
  const start = randInt(0, 2);
  const step = randInt(1, 2);
  const base = [start, start + step, start + 2 * step].map((n) => n % 10);
  return [...base, ...base, ...base];
}

const PATTERN_GENERATORS = [
  patABCABCABC,
  patAAABBBCCC,
  patAABBCCDDE,
  patABABABABC,
  patCrescente,
  patDecrescente,
  patBlocos100,
  patManyZeros,
  patDoisDigitos,
  patEscadinha,
];

// ---------- pontuação de facilidade ----------

/**
 * Score maior = mais fácil de digitar/memorizar.
 * Componentes:
 *  - dígitos únicos: menos únicos → mais fácil (contribui até +40)
 *  - repetições em bloco: pares/trios adjacentes → bônus
 *  - começa com zero: bônus grande
 *  - sequência aritmética estrita: bônus alto
 *  - alternância: bônus
 */
function scoreEasy(digits) {
  let score = 0;

  // 1) Dígitos únicos: 2 únicos = +40, 10 únicos = 0
  const unique = new Set(digits).size;
  score += Math.max(0, (10 - unique) * 5);

  // 2) Começa com zero
  if (digits[0] === 0) score += 15;

  // 3) Repetições adjacentes (pares consecutivos)
  let adjRepeats = 0;
  for (let i = 1; i < digits.length; i++) {
    if (digits[i] === digits[i - 1]) adjRepeats++;
  }
  score += adjRepeats * 3;

  // 4) Sequência aritmética estrita (crescente ou decrescente com passo fixo)
  const diffs = [];
  for (let i = 1; i < digits.length; i++) {
    diffs.push((digits[i] - digits[i - 1] + 10) % 10);
  }
  const allSameDiff = diffs.every((d) => d === diffs[0]);
  if (allSameDiff && [1, 2, 9, 8].includes(diffs[0])) score += 25;

  // 5) Bloco de 3 repetido (ABCABCABC)
  const first3 = digits.slice(0, 3).join('');
  const mid3 = digits.slice(3, 6).join('');
  const last3 = digits.slice(6, 9).join('');
  if (first3 === mid3 && mid3 === last3) score += 20;

  // 6) Trios repetidos (AAABBBCCC)
  const isAAABBBCCC =
    digits[0] === digits[1] && digits[1] === digits[2] &&
    digits[3] === digits[4] && digits[4] === digits[5] &&
    digits[6] === digits[7] && digits[7] === digits[8];
  if (isAAABBBCCC) score += 18;

  // 7) Alternância ABABAB
  const isAlt =
    digits[0] === digits[2] && digits[2] === digits[4] && digits[4] === digits[6] &&
    digits[1] === digits[3] && digits[3] === digits[5] && digits[5] === digits[7] &&
    digits[0] !== digits[1];
  if (isAlt) score += 15;

  return score;
}

// ---------- cálculo dos dígitos verificadores ----------

function calcDV(base) {
  // Primeiro DV
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += base[i] * (10 - i);
  let rest = sum % 11;
  const dv1 = rest < 2 ? 0 : 11 - rest;

  const withDv1 = [...base, dv1];
  sum = 0;
  for (let i = 0; i < 10; i++) sum += withDv1[i] * (11 - i);
  rest = sum % 11;
  const dv2 = rest < 2 ? 0 : 11 - rest;

  return [dv1, dv2];
}

// CPF inválido se todos os 11 dígitos forem iguais
function isValidCPF(digits) {
  if (digits.length !== 11) return false;
  const first = digits[0];
  return !digits.every((d) => d === first);
}

// ---------- API pública ----------

/**
 * Gera um CPF válido priorizando padrões fáceis de digitar.
 * Retorna string formatada "XXX.XXX.XXX-XX".
 */
export function generateEasyCPF() {
  const CANDIDATES = 40;
  const TOP_K = 12;

  const candidates = [];
  for (let i = 0; i < CANDIDATES; i++) {
    const gen = pick(PATTERN_GENERATORS);
    const base = gen();
    const [dv1, dv2] = calcDV(base);
    const full = [...base, dv1, dv2];
    if (!isValidCPF(full)) continue;
    candidates.push({ digits: full, score: scoreEasy(base) });
  }

  if (candidates.length === 0) {
    // Fallback ultra-raro (nunca deveria acontecer): random com bias em zeros
    const base = Array.from({ length: 9 }, (_, i) => (i === 0 ? 0 : anyDigit()));
    const [dv1, dv2] = calcDV(base);
    candidates.push({ digits: [...base, dv1, dv2], score: 0 });
  }

  // Ordena por score desc e sorteia entre top K
  candidates.sort((a, b) => b.score - a.score);
  const topK = candidates.slice(0, Math.min(TOP_K, candidates.length));
  const chosen = pick(topK);

  const s = chosen.digits.join('');
  return `${s.slice(0, 3)}.${s.slice(3, 6)}.${s.slice(6, 9)}-${s.slice(9, 11)}`;
}
